"use client";

import React, { useCallback } from "react";
import type { Toast as HotToast } from "react-hot-toast";
import { useToast } from "@/hooks/ui/useToast";
import { API_ERROR_FALLBACKS } from "@/internals/constants/errorCodes";
import {
  SSO_PROVIDER,
  SSOErrorHandlerOptions,
  SSOErrorHandlerResult,
  type SsoProviderId,
} from "@/components/SSOAuthButton/types";
import { logError } from "@/services/DatadogService/DatadogService";
import { Position } from "@/hooks/types";
import { ApiErrorMessageBuilder } from "@/utils/apiErrorHandler";
import { SsoAuthToast } from "../components/SsoAuthToast";

/**
 * Hook that handles SSO authentication errors.
 * Shows appropriate error messages based on provider and error type.
 */

/**
 * Extracts HTTP status code from error object
 */
function getApiErrorStatus(error: unknown): number | undefined {
  if (error && typeof error === "object") {
    if ("statusCode" in error) {
      const statusCode = (error as { statusCode: unknown }).statusCode;
      if (typeof statusCode === "number") return statusCode;
    }
    if ("status" in error) {
      const status = (error as { status: unknown }).status;
      if (typeof status === "number") return status;
    }
    if (
      "response" in error &&
      error.response &&
      typeof error.response === "object" &&
      "status" in error.response
    ) {
      const status = (error.response as { status: unknown }).status;
      if (typeof status === "number") return status;
    }
  }
  return undefined;
}

export const useSSOErrorHandler = ({
  provider,
  errors,
  setSsoLoading,
}: SSOErrorHandlerOptions): SSOErrorHandlerResult => {
  const { toast } = useToast();

  const handleLoginError = useCallback(
    (error: unknown) => {
      if (error) {
        // 1. DataDog logging
        logError(error instanceof Error ? error : new Error(String(error)), {
          component: "SSOAuthContainer",
          action: "loginError",
          provider,
        });

        const status = getApiErrorStatus(error);
        if (status === 400 || status === 401 || status === 404) {
          // Custom toast for auth errors with consistent ID
          toast({
            id: "sso-login-error",
            type: "custom",
            content: (t: HotToast) => (
              <SsoAuthToast
                toast={t}
                message={errors?.loginError || "Login failed"}
              />
            ),
            position: Position.top,
            duration: 5000,
          });
        } else {
          // Generic error toast for technical issues
          const errorMessage = ApiErrorMessageBuilder.buildToastMessage(error, {
            fallbackMessage: getFallbackMessage(provider, "login"),
          });
          toast({ message: errorMessage, type: "error" });
        }
        setSsoLoading(false, null);
      }
    },
    [toast, errors?.loginError, provider, setSsoLoading]
  );

  const handleRegisterError = useCallback(
    (error: unknown) => {
      if (error) {
        logError(error instanceof Error ? error : new Error(String(error)), {
          component: "SSOAuthContainer",
          action: "registerError",
          provider,
        });
        const status = getApiErrorStatus(error);

        if (status === 400 || status === 401 || status === 404) {
          // Custom toast for auth errors with consistent ID
          toast({
            id: "sso-register-error",
            type: "custom",
            content: (t: HotToast) => (
              <SsoAuthToast
                toast={t}
                message={errors?.registerError || "Registration failed"}
              />
            ),
            position: Position.top,
            duration: 5000,
          });
        } else {
          // Generic error toast for technical issues
          const errorMessage = ApiErrorMessageBuilder.buildToastMessage(error, {
            fallbackMessage: getFallbackMessage(provider, "register"),
          });
          toast({ message: errorMessage, type: "error" });
        }
        setSsoLoading(false, null);
      }
    },
    [toast, errors?.registerError, provider, setSsoLoading]
  );

  return {
    handleLoginError,
    handleRegisterError,
  };
};

/**
 * Gets the appropriate fallback error message based on provider and action
 */
function getFallbackMessage(provider: SsoProviderId, action: "login" | "register"): string {
  switch (provider) {
    case SSO_PROVIDER.GOOGLE:
      return action === "login"
        ? API_ERROR_FALLBACKS.AUTH.GOOGLE_LOGIN
        : API_ERROR_FALLBACKS.AUTH.GOOGLE_SIGNUP;

    case SSO_PROVIDER.APPLE:
      return action === "login"
        ? API_ERROR_FALLBACKS.AUTH.APPLE_LOGIN
        : API_ERROR_FALLBACKS.AUTH.APPLE_SIGNUP;

    case SSO_PROVIDER.MICROSOFT:
      return action === "login"
        ? API_ERROR_FALLBACKS.AUTH.MICROSOFT_LOGIN
        : API_ERROR_FALLBACKS.AUTH.MICROSOFT_SIGNUP;

    default:
      // TODO: Add generic fallback messages
      return action === "login"
        ? "" //Authentication failed....
        : ""; // Registration failed......
  }
}
